/**
 * Every page load owns a fresh writer key inside its cache scope, so concurrent
 * tabs never overwrite each other's paused offline writes. These helpers keep
 * the number of writer snapshots bounded and let restore pick the newest one
 * without deserializing the rest.
 */
import type {
  PersistedClient,
  Persister,
} from '@tanstack/query-persist-client-core'

/** `persistClient` resolves `true` only once the snapshot is durably stored;
 * a fenced or failed write resolves `false`. */
export interface SnapshotPersister extends Omit<Persister, 'persistClient'> {
  persistClient: (client: PersistedClient) => Promise<boolean>
}

export interface WriterSnapshot {
  timestamp: number
  /** Dehydrated paused offline mutations held by the snapshot. */
  mutations: number
}

export type WriterIndex = Record<string, WriterSnapshot>

/** Adopted = this writer restored exactly that snapshot, mutations included. */
export interface AdoptedWriter {
  key: string
  timestamp: number
}

/** `schema-` keeps the index out of older builds' writer scans. */
export const writerIndexKey = (scopePrefix: string) =>
  `${scopePrefix}schema-index-v1`

export function isScopeWriterKey(
  key: unknown,
  scopePrefix: string,
  ownKey: string,
): key is string {
  return (
    typeof key === 'string' &&
    key !== ownKey &&
    key.startsWith(scopePrefix) &&
    !key.slice(scopePrefix.length).startsWith('schema-')
  )
}

export function describeWriterSnapshot(
  client: PersistedClient,
): WriterSnapshot {
  const mutations = client.clientState?.mutations
  return {
    timestamp: typeof client.timestamp === 'number' ? client.timestamp : 0,
    mutations: Array.isArray(mutations) ? mutations.length : 0,
  }
}

export function parseWriterIndex(value: unknown): WriterIndex {
  const index: WriterIndex = {}
  if (!value || typeof value !== 'object' || Array.isArray(value)) return index
  for (const [key, entry] of Object.entries(value)) {
    if (
      entry &&
      typeof entry === 'object' &&
      Number.isFinite((entry as WriterSnapshot).timestamp) &&
      Number.isInteger((entry as WriterSnapshot).mutations) &&
      (entry as WriterSnapshot).mutations >= 0
    )
      index[key] = {
        timestamp: (entry as WriterSnapshot).timestamp,
        mutations: (entry as WriterSnapshot).mutations,
      }
  }
  return index
}

/**
 * Another writer's snapshot is superseded once this writer has durably written
 * a snapshot at least as new, and it either carries no paused offline writes
 * or is the exact snapshot this writer restored (so its writes were hydrated
 * here). Anything else, including a live tab's newer snapshot or paused work
 * nobody adopted, is kept.
 */
export function isSupersededWriter(
  key: string,
  entry: WriterSnapshot,
  own: WriterSnapshot,
  adopted: AdoptedWriter | null,
): boolean {
  if (entry.timestamp > own.timestamp) return false
  return (
    entry.mutations === 0 ||
    (adopted?.key === key && adopted.timestamp === entry.timestamp)
  )
}
